/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IImageSettings } from '@msdyn365-commerce/core';

import * as React from 'react';
import { WineClubDataStore } from './wine-club-data';
import msdyn365Commerce from '@msdyn365-commerce/core-internal';
import { IWineClubSignupData } from './wine-club-signup.data';
import { IWineClubSignupProps } from './wine-club-signup.props.autogenerated';

import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { IWineClub, wineClubSetup } from '../../settings/app.settings';
import { BusLoader, LoadBus } from '../../Utilities/event-bus';


interface IWineclubState {
    wineclubWidth?: string;
}
export interface IWineClubSignupViewProps extends IWineClubSignupProps<IWineClubSignupData> {
    clubInfo: IWineClub[];
    clubImageSettings?: IImageSettings;
    variantImageSettings?: IImageSettings;
    moduleControl: renderables;
    debug: string;
    triggerRender(): void;
}

// !! TODO: ADD PICKUP WHEN MOCK IS READY

export type renderables = {
    CHOOSE_CLUB: boolean;
    CHOOSE_PREFERENCE: boolean;
    SET_MEMBERSHIP: boolean;
    SET_SHIPPING: boolean;
    TERMS: boolean;
    SIGNUP: boolean;
};

/**
 *
 * WineClubSignup component
 * @extends {React.Component<IWineClubSignupProps<IWineClubSignupData>>}
 */
@LoadBus('tos')
@observer
class WineClubSignup extends React.Component<IWineClubSignupProps<IWineClubSignupData>, IWineclubState> {

    @observable private renderables: renderables = {
        CHOOSE_CLUB: false,
        CHOOSE_PREFERENCE: false,
        SET_MEMBERSHIP: false,
        SET_SHIPPING: false,
        TERMS: false,
        SIGNUP: false
    };

    private _clubStore: WineClubDataStore;
    private bus: BusLoader | undefined;

    public constructor(props: IWineClubSignupProps<IWineClubSignupData>) {
        super(props);
        this.bus!.subscribe('tos-agreement-changed', (agreeState: boolean) => {
            const store = this._clubStore.get();
            store.tosAgree = agreeState;
            this._clubStore.edit(store);
        });

        this._clubStore = WineClubDataStore.getInstance();
        this._runClubValidation();
        this._triggerRender = this._triggerRender.bind(this);
        this.state = { wineclubWidth: '200px'};
    }

    public componentDidMount(): void {
        const query = this.props.context.request.query;
        if (query?.refId && query.refId !== '') {
            sessionStorage.setItem('refId', query.refId);
        }
        WineClubDataStore.getInstance(() => {
            this._updateRenderables();
        });

        WineClubDataStore.getInstance().reset();

        // we need to set the renderables once
        this._updateRenderables();
        if (this.props.config.clubitemWidth && this.props.config.clubitemWidth !== '') {
            this.setState({ wineclubWidth: this.props.config.clubitemWidth });
        }
    }

    public componentDidUpdate(prevProps: {}, prevState: IWineclubState): void {
        if (msdyn365Commerce.isBrowser && (prevState.wineclubWidth !== this.state.wineclubWidth)) {
            this._updateRootCssVariable('--clubItemWidth', `${this.state.wineclubWidth}`);
          }
    }

    public render(): JSX.Element | null {
        return this.props.renderView({
            clubInfo: this.props.app.config.wineClubSetup || [],
            clubImageSettings: this.props.config.clubImageSettings,
            variantImageSettings: this.props.config.variantImageSettings,
            moduleControl: this.renderables,
            debug: JSON.stringify(this._clubStore.get()),
            triggerRender: this._triggerRender,
            ...this.props
        });
    }

    private _updateRenderables(): void {
        const store = this._clubStore.get();

        // Detect if there is only one club set up.
        const wineClubs = this.props.app.config.wineClubSetup;
        const isOnlyClub = (wineClubs.length === 1);

        // If there is only one club and nothing is selected, select the club.
        if (isOnlyClub && store.clubId === null) {
            this._clubStore.edit({
                ...store,
                clubId: 0
            });
        }

        this.renderables = {
            CHOOSE_CLUB: !isOnlyClub, // If there is only one club, skip rendering the list of clubs.
            CHOOSE_PREFERENCE: store.clubId !== null,
            SET_MEMBERSHIP: store.clubId !== null && store.variantPreference !== null,
            SET_SHIPPING: store.clubId !== null && store.variantPreference !== null && store.forMyself !== null,
            TERMS: store.clubId !== null && store.variantPreference !== null && store.forMyself !== null,
            SIGNUP: store.clubId !== null && store.variantPreference !== null && store.forMyself !== null
        };
    }

    private _runClubValidation(): void {
        const store = this._clubStore.get();
        const clubSignup = this.props.app.config.wineClubSetup as wineClubSetup;

        // reset if out of bounds on club
        if (store.clubId && store.clubId > clubSignup.length - 1) {
            this._clubStore.reset();
        }

        // we don't need one for variant because thats done via a find, not direct
        // index lookup
    }

    private _updateRootCssVariable = (name: string, value: string) => {
        let style = document.querySelector(':root > style');
        if (!style) {
            style = document.createElement('style');
            style.appendChild(document.createTextNode(''));
            document.head.appendChild(style);
        }
        style.textContent = `:root { ${name}: ${value}; }`;
    };

    /**
     * used within the view extension to retrigger a full component re-render
     *
     * preserves wine-club's code decoupling
     */
    private _triggerRender(): void {
        this.setState({ });
    }
}

export default WineClubSignup;
